import { UserTable } from "@/routes/_dashboard/users/-components/user-table";
import { organisationUsersQueryOptions } from "@/server/users";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useContext, useEffect, useMemo, useCallback } from "react";
import { DashboardContext } from "@/components/dashboard-layout";
import { SidebarUser } from "@/routes/_dashboard/users/-components/user-sidebar";
import { minuteCounterOverridesQueryOptions } from "@/server/settings";
import { useRightSidebarClose } from "@/hooks/use-right-sidebar-close";
import { useRightSidebarOpen } from "@/hooks/use-right-sidebar-open";
import { CrudEntry } from "@/lib/mma/types.gen";
import { checkIfUserMayWrite, ContextUser } from "@/lib/utils";
import { usePermissionRedirect } from "@/hooks/use-permission-redirect";

export const Route = createFileRoute("/_dashboard/organisations/$orgId/users")({
  component: RouteComponent,
  loader: async ({ params, context }: { params: { orgId: string }; context: unknown }) => {
    return {
      orgId: params.orgId,
      crumb: "Users",
      user: (context as { user?: ContextUser }).user,
      userProfileId: (context as { user?: { profile: string } }).user?.profile,
      accessControl: (context as { user?: { accessControl: Record<string, CrudEntry> } }).user?.accessControl,
    };
  },
});

function RouteComponent() {
  const { orgId } = Route.useParams();
  const { user, userProfileId, accessControl } = Route.useLoaderData();
  usePermissionRedirect({ user, permission: "USER", redirectTo: "/organisations" });
  const mayWrite = checkIfUserMayWrite(userProfileId, accessControl, "USER");
  const searchParams = useSearch({ strict: false }) as {
    search?: string;
    expire?: number;
    id?: string;
  };
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const context = useContext(DashboardContext);
  const closeRightSidebar = useRightSidebarClose();
  const openRightSidebar = useRightSidebarOpen();
  const expire = useMemo(() => {
    if (typeof searchParams.expire === "number") {
      return searchParams.expire;
    }
    if (searchParams.expire) {
      const expireDate = new Date(searchParams.expire);
      if (!isNaN(expireDate.getTime())) {
        return parseInt(expireDate.getTime().toFixed(0));
      }
    }
    return undefined;
  }, [searchParams.expire]);

  const organisationUsersQueryOptionsMemo = useMemo(
    () => organisationUsersQueryOptions(Number(orgId ?? 0), searchParams.search, expire),
    [orgId, searchParams.search, expire]
  );
  const organisationUsersQuery = useQuery(organisationUsersQueryOptionsMemo);
  const organisationUsers = organisationUsersQuery.data;
  const minuteCounterOverridesQueryOptionsMemo = useMemo(() => minuteCounterOverridesQueryOptions, []);
  const minuteCounterOverridesQuery = useQuery(minuteCounterOverridesQueryOptionsMemo);
  const minuteCounterOverrides = minuteCounterOverridesQuery.data?.minuteCounterOverrides;

  useEffect(() => {
    if (!orgId) return;
    void organisationUsersQuery.refetch();
    void minuteCounterOverridesQuery.refetch();
  }, [orgId]);

  if (!context) {
    console.error("DashboardContext is null. Ensure DashboardLayout wraps this component.");
    return <div>Error: DashboardContext is not available.</div>;
  }

  const handleAddUser = useCallback(() => {
    navigate({
      to: "/organisations/$orgId/users",
      params: { orgId: orgId ?? 0 },
      search: (prev) => ({ ...prev, id: "new" }),
    });
  }, [navigate, orgId]);

  const closeAfterSave = useCallback(() => {
    closeRightSidebar([
      "id",
      "tab",
      "transactionsPage",
      "transactionsPerPage",
      "transactionsSortBy",
      "transactionsSortDir",
    ]);
  }, [closeRightSidebar]);

  const handleUserCreated = useCallback(() => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && key[0] === "users";
      },
    });
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && key[0] === "organisationUsers" && key[1] === Number(orgId ?? 0);
      },
    });
    closeAfterSave();
  }, [queryClient, orgId, closeAfterSave]);

  const handleUserUpdated = useCallback(() => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && key[0] === "users";
      },
    });
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && key[0] === "organisationUsers" && key[1] === Number(orgId ?? 0);
      },
    });
    closeAfterSave();
  }, [queryClient, orgId, closeAfterSave]);

  const handleUserDeleted = useCallback(() => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && key[0] === "users";
      },
    });
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && key[0] === "organisationUsers" && key[1] === Number(orgId ?? 0);
      },
    });
    closeAfterSave();
  }, [queryClient, orgId, closeAfterSave]);

  const paramKeys = useMemo(
    () =>
      ["id", "tab", "transactionsPage", "transactionsPerPage", "transactionsSortBy", "transactionsSortDir"] as const,
    []
  );

  // Memoize the sidebar component to prevent unnecessary re-renders
  const sidebarComponent = useMemo(() => {
    if (!searchParams.id) return null;

    if (searchParams.id === "new") {
      return (
        <SidebarUser
          userId={null}
          organisationId={Number(orgId ?? 0)}
          onUserCreated={handleUserCreated}
          isOrganisationRouteUser={true}
          minuteCounterOverrides={minuteCounterOverrides}
          closeAfterSave={closeAfterSave}
          mayWrite={mayWrite}
        />
      );
    }

    if (organisationUsers?.length) {
      const user = organisationUsers.find((u) => u.id === searchParams.id);
      if (user) {
        return (
          <SidebarUser
            userId={Number(user.id)}
            organisationId={Number(orgId ?? 0)}
            onUserUpdated={handleUserUpdated}
            onUserDeleted={handleUserDeleted}
            isOrganisationRouteUser={true}
            minuteCounterOverrides={minuteCounterOverrides}
            closeAfterSave={closeAfterSave}
          />
        );
      }
    }

    return null;
  }, [
    searchParams.id,
    orgId,
    organisationUsers,
    handleUserCreated,
    handleUserUpdated,
    handleUserDeleted,
    closeAfterSave,
    minuteCounterOverrides,
    mayWrite,
  ]);

  useEffect(() => {
    if (!context || !searchParams.id) {
      return;
    }

    if (!sidebarComponent) {
      return;
    }

    const hasPermission = searchParams.id !== "new" || mayWrite;

    openRightSidebar({
      component: sidebarComponent,
      paramKeys: paramKeys as any,
      shouldClose: false,
      permissionCheck: !hasPermission,
      onPermissionDenied: () => closeAfterSave(),
    });
  }, [sidebarComponent, mayWrite, searchParams.id, openRightSidebar, closeAfterSave, paramKeys]);

  useEffect(() => {
    return () => {
      closeRightSidebar();
    };
  }, [orgId, closeRightSidebar]);

  return (
    <UserTable
      users={organisationUsers}
      organisationId={Number(orgId ?? 0)}
      handleAddUser={handleAddUser}
      isLoading={organisationUsersQuery.isLoading || organisationUsersQuery.isFetching}
      mayWrite={mayWrite}
    />
  );
}
